import { Container, type ContainerOptions } from "../core/Container.js";
import type { KeyEventArgs, WheelEventArgs } from "../core/events.js";
import { clamp } from "../../common/geometry.js";
import type { Size } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { GlyphButton } from "./GlyphButton.js";
import { TextBox } from "./TextBox.js";

export interface NumericUpDownOptions extends ContainerOptions {
  value?: number;
  minimum?: number;
  maximum?: number;
  /** How far one step of the buttons, arrows or wheel moves the value. */
  increment?: number;
  /** Decimal places to show, and to round to. */
  decimals?: number;
  /** Shown after the number: "%", " ms", "x". */
  suffix?: string;
  /** Wrap round to the other end instead of stopping. */
  wrap?: boolean;
  readOnly?: boolean;
  font?: FontSpec;
  onChange?: (value: number) => void;
}

const REPEAT_DELAY_MS = 400;
const REPEAT_INTERVAL_MS = 60;

/**
 * A number with a pair of stepper buttons — WinForms' NumericUpDown.
 *
 * Typed text is not parsed as it is typed: half of "-12" is "-", and
 * committing that would fight the user for the field. The text is read back
 * when the field loses focus or Enter is pressed, and whatever it makes of it
 * is clamped, rounded and written back formatted. Text that parses to nothing
 * reverts rather than becoming zero, so a slip of the keyboard does not
 * silently reset a setting.
 */
export class NumericUpDown extends Container {
  #field: TextBox;
  #up: GlyphButton;
  #down: GlyphButton;

  #value = 0;
  #minimum: number;
  #maximum: number;
  #repeatTimer: ReturnType<typeof setTimeout> | number = 0;

  increment: number;
  decimals: number;
  suffix: string;
  wrap: boolean;

  constructor(options: NumericUpDownOptions = {}) {
    super({ width: 110, height: 28, ...options });
    this.#minimum = options.minimum ?? 0;
    this.#maximum = Math.max(this.#minimum, options.maximum ?? 100);
    this.increment = options.increment ?? 1;
    this.decimals = Math.max(0, Math.min(options.decimals ?? 0, 10));
    this.suffix = options.suffix ?? "";
    this.wrap = options.wrap ?? false;
    this.#value = this.#coerce(options.value ?? this.#minimum);

    this.#field = new TextBox({
      text: this.#format(this.#value),
      font: options.font,
      readOnly: options.readOnly ?? false,
      // The container draws the frame, so the field inside it must not.
      frame: false,
    });
    this.#field.on("blur", () => this.commit());
    this.#field.on("keydown", (e) => this.#onFieldKey(e));

    this.#up = this.#stepper("chevron-up", 1);
    this.#down = this.#stepper("chevron-down", -1);
    this.controls.addRange([this.#field, this.#up, this.#down]);

    if (options.onChange) this.on("change", () => options.onChange!(this.#value));
    this.on("wheel", (e) => this.#onWheel(e));
    this.on("resize", () => this.#layout());
  }

  #stepper(glyph: string, direction: 1 | -1): GlyphButton {
    const button = new GlyphButton({ glyph, glyphScale: 0.5, tabStop: false });
    // Press-and-hold repeats. A stepper that only fired on release would make
    // walking a value from 0 to 60 a sixty-click job.
    button.on("pointerdown", () => this.#beginRepeat(direction));
    // Both, and for the same reason ScrollBar stops its own repeat both ways:
    // a pointer that leaves the button mid-hold is no longer pressing it.
    button.on("pointerup", () => this.#endRepeat());
    button.on("pointerleave", () => this.#endRepeat());
    button.on("enabledchanged", () => this.#endRepeat());
    return button;
  }

  // --- value ---------------------------------------------------------------

  get value(): number {
    return this.#value;
  }
  set value(v: number) {
    const next = this.#coerce(v);
    // The text still needs rewriting even when the number did not change:
    // "007" and "7" are the same value and only one of them is the format.
    this.#field.text = this.#format(next);
    if (next === this.#value) return;
    this.#value = next;
    this.emit("valuechanged");
    this.emit("change");
    this.invalidate();
  }

  get minimum(): number {
    return this.#minimum;
  }
  set minimum(v: number) {
    this.setRange(v, this.#maximum);
  }

  get maximum(): number {
    return this.#maximum;
  }
  set maximum(v: number) {
    this.setRange(this.#minimum, v);
  }

  setRange(minimum: number, maximum: number): void {
    this.#minimum = Number.isFinite(minimum) ? minimum : 0;
    this.#maximum = Math.max(this.#minimum, Number.isFinite(maximum) ? maximum : this.#minimum);
    this.value = this.#value;
  }

  get readOnly(): boolean {
    return this.#field.readOnly;
  }
  set readOnly(v: boolean) {
    this.#field.readOnly = v;
  }

  /** The editable field, for a caller that wants to select or place a caret. */
  get field(): TextBox {
    return this.#field;
  }

  /** Move the value by `count` increments. */
  step(count: number): void {
    const next = this.#value + count * this.increment;
    if (!this.wrap) {
      this.value = next;
      return;
    }
    const span = this.#maximum - this.#minimum;
    if (span <= 0) {
      this.value = this.#minimum;
      return;
    }
    // One increment past the top is the bottom, not the top again.
    if (next > this.#maximum) this.value = this.#minimum;
    else if (next < this.#minimum) this.value = this.#maximum;
    else this.value = next;
  }

  /**
   * Read the text back as a number. Called on blur and on Enter; safe to call
   * at any time. Text that parses to nothing puts the old value back.
   */
  commit(): void {
    const parsed = parseNumber(this.#field.text, this.suffix);
    this.value = parsed === null ? this.#value : parsed;
  }

  #coerce(v: number): number {
    if (!Number.isFinite(v)) return this.#value;
    const clamped = clamp(v, this.#minimum, this.#maximum);
    return this.decimals > 0 ? Number(clamped.toFixed(this.decimals)) : Math.round(clamped);
  }

  #format(v: number): string {
    return `${v.toFixed(this.decimals)}${this.suffix}`;
  }

  // --- interaction ---------------------------------------------------------

  #beginRepeat(direction: 1 | -1): void {
    this.#endRepeat();
    this.step(direction);
    const tick = (): void => {
      this.step(direction);
      this.#repeatTimer = setTimeout(tick, REPEAT_INTERVAL_MS);
    };
    this.#repeatTimer = setTimeout(tick, REPEAT_DELAY_MS);
  }

  #endRepeat(): void {
    if (!this.#repeatTimer) return;
    clearTimeout(this.#repeatTimer as ReturnType<typeof setTimeout>);
    this.#repeatTimer = 0;
  }

  #onFieldKey(e: KeyEventArgs): void {
    switch (e.key) {
      case "ArrowUp":
        this.step(1);
        e.handled = true;
        break;
      case "ArrowDown":
        this.step(-1);
        e.handled = true;
        break;
      case "PageUp":
        this.step(10);
        e.handled = true;
        break;
      case "PageDown":
        this.step(-10);
        e.handled = true;
        break;
      case "Enter":
        this.commit();
        this.#field.selectAll();
        e.handled = true;
        break;
      case "Escape":
        // Put the committed value back and give up on whatever was typed.
        this.#field.text = this.#format(this.#value);
        e.handled = true;
        break;
      default:
        break;
    }
  }

  #onWheel(e: WheelEventArgs): void {
    if (e.deltaY === 0 || !this.#field.focused) return;
    // Only when the field has focus: a wheel over an unfocused spin box on a
    // long settings page means "scroll the page", not "change this setting".
    this.step(-Math.sign(e.deltaY));
    e.handled = true;
  }

  // --- layout --------------------------------------------------------------

  #layout(): void {
    const theme = this.desktop?.theme;
    if (!theme) return;
    const r = this.localRect;
    const buttons = theme.metrics.spinButtonWidth;
    const border = 1;
    const half = Math.floor((r.height - border * 2) / 2);

    this.#field.setBounds(
      border,
      border,
      Math.max(0, r.width - buttons - border * 2),
      r.height - border * 2,
    );
    this.#up.setBounds(r.width - buttons - border, border, buttons, half);
    this.#down.setBounds(r.width - buttons - border, border + half, buttons, r.height - border * 2 - half);
  }

  override onChildrenChanged(): void {
    super.onChildrenChanged();
    this.#layout();
  }


  /**
   * One theme control-height tall; the width stays whatever it was given.
   *
   * A field has no content width worth measuring — it is as wide as the form wants it, not as
   * wide as the text someone happens to have typed — so the only interesting axis is the one the
   * theme decides.
   */
  override preferredSize(_available: Size): Size {
    const desktop = this.desktop;
    if (!desktop) return this.size;
    return { width: this.width, height: desktop.theme.metrics.controlHeight };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    this.#layout();
    theme.drawTextBox(rc, this.localRect, {
      ...this.visualState(),
      focused: this.#field.focused,
    });
  }
}

/**
 * Read a number out of what someone typed.
 *
 * Tolerant on purpose: the suffix the field itself wrote is stripped, spaces
 * and thousands separators go, and a lone "-" or "" is not a number at all
 * rather than being taken as zero.
 */
export function parseNumber(text: string, suffix = ""): number | null {
  let body = text.trim();
  if (suffix && body.endsWith(suffix)) body = body.slice(0, body.length - suffix.length);
  body = body.replace(/[\s,]/g, "");
  if (body === "" || body === "-" || body === "+" || body === ".") return null;
  const value = Number(body);
  return Number.isFinite(value) ? value : null;
}
